import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useTranslation } from "react-i18next";
import { useGetAttachmentUrlMutation } from "@/features/Global/api";
import { hexToRgba } from "@/Components/helpers/util";

/**
 * Component to handle loading and displaying attachments
 * @param {Object} attachment - The attachment object containing url, fileName, etc.
 * @param {boolean} isImage - Whether the attachment is recognized as an image
 * @param {boolean} needsFetch - Whether the URL needs to be fetched from the server
 */
const AttachmentLoader = ({ attachment, isImage, needsFetch }) => {
  const { t } = useTranslation(["bugherd"]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Start with false
  const [error, setError] = useState(false);
  const [postAttachmentKey] = useGetAttachmentUrlMutation();

  useEffect(() => {
    // Reset states when attachment changes
    setError(false);
    //Case 0: Public URL is there
    if (attachment && attachment.url && attachment.url.startsWith("https:")) {
      setPreviewUrl(attachment.url);
      setIsLoading(false);
      return;
    }
    // Case 1: Base64 URL (either new upload or annotated image)
    if (attachment.url && attachment.url.startsWith("data:")) {
      setPreviewUrl(attachment.url);
      setIsLoading(false);
      return;
    }

    // Case 2: Server URL that needs to be fetched
    if (needsFetch && attachment.url) {
      setIsLoading(true);

      // Call the API to get the actual URL
      postAttachmentKey({
        key: attachment.url,
      })
        .unwrap()
        .then((res) => {
          if (res?.data?.decryptedData?.url) {
            setPreviewUrl(res.data.decryptedData.url);
          } else {
            setError(true);
          }
        })
        .catch((err) => {
          console.error("Error fetching attachment URL:", err);
          setError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
      return;
    }

    // Case 3: Already resolved URL (e.g. serverUrl)
    if (isImage && attachment.serverUrl) {
      setPreviewUrl(attachment.serverUrl);
      setIsLoading(false);
      return;
    }

    // Case 4: No URL or not an image
    setPreviewUrl(null);
    setIsLoading(false);
  }, [attachment, needsFetch, isImage]);

  const handleImageError = () => {
    console.error("Image failed to load:", previewUrl);
    setError(true);
  };

  // Content rendering based on conditions
  if (isLoading) {
    // Only show loading when we're actually fetching from the server
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <CircularProgress size={24} color="primary" />
      </Box>
    );
  }

  // For new uploads with base64, show the image immediately with an upload indicator
  if (isImage && attachment.url?.startsWith("data:")) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 1,
          bgcolor: "#f5f5f5",
          height: 200,
          position: "relative",
          "&:hover": {
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          },
        }}
      >
        <img
          src={attachment.url}
          alt={attachment.fileName || attachment.name}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
          onError={handleImageError}
        />

        {/* Upload indicator (small badge in corner instead of overlay) */}
        {/* {attachment.isUploading && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              bgcolor: (theme) => hexToRgba(theme.palette.info.main, 0.9),
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: "4px",
              fontSize: "10px",
              fontWeight: "bold",
              boxShadow: 1,
            }}
          >
            <CircularProgress size={10} sx={{ color: "white" }} />
            {t("Uploading")}
          </Box>
        )} */}

        {/* Annotation indicator */}
        {attachment.isAnnotated && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: (theme) => hexToRgba(theme.palette.primary.main, 0.9),
              color: "white",
              px: 1,
              py: 0.25,
              borderRadius: "4px",
              fontSize: "10px",
              fontWeight: "bold",
              boxShadow: 1,
            }}
          >
            {t("Annotated")}
          </Box>
        )}

        {/* Failed upload indicator */}
        {attachment.uploadFailed && (
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              left: 8,
              bgcolor: (theme) => hexToRgba(theme.palette.error.main, 0.9),
              color: "white",
              px: 1,
              py: 0.25,
              borderRadius: "4px",
              fontSize: "10px",
              fontWeight: "bold",
              boxShadow: 1,
            }}
          >
            {t("Upload failed")}
          </Box>
        )}
      </Box>
    );
  }

  // Previously fetched image from server
  if (isImage && previewUrl) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 1,
          bgcolor: "#f5f5f5",
          height: 200,
          position: "relative",
          "&:hover": {
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          },
        }}
      >
        <img
          src={previewUrl}
          alt={attachment.fileName || attachment.name}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
          onError={handleImageError}
        />

        {/* Annotation indicator */}
        {attachment.isAnnotated && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: (theme) => hexToRgba(theme.palette.primary.main, 0.9),
              color: "white",
              px: 1,
              py: 0.25,
              borderRadius: "4px",
              fontSize: "10px",
              fontWeight: "bold",
              boxShadow: 1,
            }}
          >
            {t("Annotated")}
          </Box>
        )}
      </Box>
    );
  }

  // Error state
  if (error || attachment.uploadFailed) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <AttachFileIcon sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
        <Typography variant="caption" color="error.main">
          {attachment.uploadFailed
            ? t("Upload failed, using local version")
            : t("Failed to load attachment")}
        </Typography>
      </Box>
    );
  }

  // Fallback for non-images or when no URL is available
  return (
    <Box
      sx={{
        height: 200,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        bgcolor: "#f5f5f5",
      }}
    >
      <AttachFileIcon sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
      <Typography variant="caption" color="text.secondary">
        {t("No preview available")}
      </Typography>
    </Box>
  );
};

export default AttachmentLoader;
